import type {
  MerchantProduct,
  ProductMerchantApiItem,
  SalesMetrics,
  PromotionMetrics,
  PricingMetrics,
  CustomerMetrics,
  VariantCombination,
  VariantOption,
  Bundle,
  Certification,
  Shipper,
  SalesMetricsApi,
  PromotionMetricsApi,
  PricingMetricsApi,
  CustomerMetricsApi,
  VariantCombinationApi,
  VariantOptionApi,
  BundleApi,
  CertificationApi,
  ShipperApi
} from "../types"

/**
 * Transform sales metrics from snake_case API format to camelCase
 */
export function transformSalesMetrics(api: SalesMetricsApi): SalesMetrics {
  return {
    salesQty30d: api.sales_qty_30d,
    salesRevenue30d: api.sales_revenue_30d,
    avgDailySalesQty30d: api.avg_daily_sales_qty_30d,
    avgDailySalesRevenue30d: api.avg_daily_sales_revenue_30d,
    salesQty100d: api.sales_qty_100d,
    salesRevenue100d: api.sales_revenue_100d,
    avgDailySalesQty100d: api.avg_daily_sales_qty_100d,
    avgDailySalesRevenue100d: api.avg_daily_sales_revenue_100d,
    lastOrderDate: api.last_order_date,
    salesVelocity: api.sales_velocity
  }
}

/**
 * Transform promotion metrics from snake_case API format to camelCase
 */
export function transformPromotionMetrics(api: PromotionMetricsApi): PromotionMetrics {
  return {
    lastPromotionDate: api.last_promotion_date,
    lastPromotionSalesQty: api.last_promotion_sales_qty,
    lastPromotionSalesRevenue: api.last_promotion_sales_revenue,
    promotionConversionRate: api.promotion_conversion_rate
  }
}

/**
 * Transform pricing metrics from snake_case API format to camelCase
 */
export function transformPricingMetrics(api: PricingMetricsApi): PricingMetrics {
  return {
    averageSellingPrice: api.average_selling_price,
    averageMargin: api.average_margin,
    highestDiscountPercent: api.highest_discount_percent,
    averageDiscountPercent: api.average_discount_percent
  }
}

/**
 * Transform customer metrics from snake_case API format to camelCase
 */
export function transformCustomerMetrics(api: CustomerMetricsApi): CustomerMetrics {
  return {
    repeatPurchaseRate: api.repeat_purchase_rate,
    newCustomerAcquisitionRate: api.new_customer_acquisition_rate,
    customerReviewAvgRating: api.customer_review_avg_rating,
    customerReviewCount: api.customer_review_count
  }
}

/**
 * Transform variant option from snake_case API format to camelCase
 */
export function transformVariantOption(api: VariantOptionApi): VariantOption {
  return {
    id: api.id,
    name: api.name,
    index: api.index,
    optionList: api.option_list.map(option => ({
      option: option.option,
      image: option.image
    }))
  }
}

/**
 * Transform variant combination from snake_case API format to camelCase
 */
export function transformVariantCombination(
  api: VariantCombinationApi,
  variantOptions: VariantOptionApi[] = []
): VariantCombination {
  return {
    id: api.id,
    sku: api.sku,
    isActive: api.is_active,
    stock: api.stock,
    price: api.price,
    weight: api.weight,
    dimension: api.dimension,
    tierIndex: api.tier_index,
    tierVariation: api.tier_variation
  }
}

/**
 * Transform bundle from snake_case API format to camelCase
 */
export function transformBundle(api: BundleApi): Bundle {
  return {
    id: api.id,
    name: api.name,
    items: api.items.map(item => ({
      productId: item.product_id,
      quantity: item.quantity
    }))
  }
}

/**
 * Transform certification from snake_case API format to camelCase
 */
export function transformCertification(api: CertificationApi): Certification {
  return {
    id: api.id,
    name: api.name,
    issuedBy: api.issued_by,
    validUntil: api.valid_until
  }
}

/**
 * Transform shipper from snake_case API format to camelCase
 */
export function transformShipper(api: ShipperApi): Shipper {
  return {
    id: api.id,
    name: api.name,
    isActive: api.is_active
  }
}

/**
 * Find first available variant image from any variant option
 */
export function findFirstVariantImage(variantOptions: VariantOptionApi[]): string | undefined {
  if (!variantOptions?.length) {
    return undefined
  }

  for (const variantOption of variantOptions) {
    if (variantOption.option_list?.length) {
      for (const option of variantOption.option_list) {
        if (option.image && option.image.trim()) {
          return option.image
        }
      }
    }
  }

  return undefined
}

/**
 * Resolve variant image by mapping tier_index to variant_options with bounds checking
 */
export function resolveVariantImage(
  variantCombination: VariantCombinationApi,
  variantOptions: VariantOptionApi[]
): string | undefined {
  if (!variantCombination.tier_index || !variantOptions?.length) {
    return undefined
  }

  // Try to find an image from any tier level with bounds checking
  for (let tierLevel = 0; tierLevel < variantCombination.tier_index.length; tierLevel++) {
    const optionIndex = variantCombination.tier_index[tierLevel]
    const variantOption = variantOptions.find(opt => opt.index === tierLevel)
    
    // Add bounds checking for option_list array
    if (variantOption && variantOption.option_list && optionIndex < variantOption.option_list.length) {
      const option = variantOption.option_list[optionIndex]
      if (option?.image && option.image.trim()) {
        return option.image
      }
    }
  }

  // Fallback: find first available variant image
  return findFirstVariantImage(variantOptions)
}

/**
 * Convert snake_case API response to camelCase frontend format
 */
export function transformApiItem(apiItem: ProductMerchantApiItem): MerchantProduct {
  return {
    // Core Product Information
    id: apiItem.id,
    name: apiItem.name,
    nameInsensitive: apiItem.name_insensitive,
    sku: apiItem.sku,
    brandName: apiItem.brand_name,
    description: apiItem.description,
    isActive: apiItem.is_active,
    isDangerousProduct: apiItem.is_dangerous_product,
    isProductHasVariants: apiItem.is_product_has_variants,
    isBundle: apiItem.is_bundle,

    // Categories and Classification
    category: Array.isArray(apiItem?.category)
      ? apiItem.category.map(cat => ({
          id: cat.id,
          level: cat.level,
          hasChildren: cat.has_children
        }))
      : [],
    attributes: apiItem.attributes,
    tags: apiItem.tags,
    promotionTags: apiItem.promotion_tags,

    // Physical Properties
    dimension: apiItem.dimension,
    weight: apiItem.weight,
    minOrder: apiItem.min_order,
    packingInstruction: apiItem.packing_instruction,

    // Pre-order Configuration
    preOrder: apiItem.pre_order
      ? {
          isEnabled: apiItem.pre_order.is_enabled,
          type: apiItem.pre_order.type,
          shipInDays: apiItem.pre_order.ship_in_days
        }
      : undefined,

    // Analytics Metrics
    promotionScore: apiItem.promotion_score,
    salesMetrics: transformSalesMetrics(apiItem.sales_metrics),
    promotionMetrics: transformPromotionMetrics(apiItem.promotion_metrics),
    pricingMetrics: transformPricingMetrics(apiItem.pricing_metrics),
    customerMetrics: transformCustomerMetrics(apiItem.customer_metrics),

    // Media
    mainImage: apiItem.main_image,
    photos: apiItem.photos.map(photo => ({
      url: photo.url,
      hash: photo.hash,
      isPrimary: photo.is_primary,
      isActive: photo.is_active
    })),

    // Variants
    variantCombinations: apiItem.variant_combinations.map(combo =>
      transformVariantCombination(combo, apiItem.variant_options)
    ),
    variantOptions: apiItem.variant_options.map(transformVariantOption),

    // Optional Features
    bundles: apiItem.bundles?.map(transformBundle),
    certifications: apiItem.certifications?.map(transformCertification),
    shippers: apiItem.shippers?.map(transformShipper),

    // Metadata
    clientId: apiItem.client_id,
    companyId: apiItem.company_id,
    createdAt: apiItem.created_at,
    updatedAt: apiItem.updated_at,
    createdBy: apiItem.created_by,
    updatedBy: apiItem.updated_by
  }
}